import RecordTestModal from '../../../components/RecordTestModal';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import AssetImportModal from './AssetImportModal';
import AssetFormModal from './AssetFormModal';
import AssetTableDesktop from './AssetTableDesktop';
import AssetMobileCards from './AssetMobileCards';
import { useAssets } from '../hooks/useAssets';
import {
  ASSET_TYPES,
  SORT_OPTIONS,
  ELECTRICAL_ASSET_TYPES,
  FITMENTS_ASSET_TYPES,
  ASSET_FREQUENCY_MAP,
  ASSET_TYPE_DEFAULTS,
  CONDITION_RATINGS,
} from '../constants/assetConfig';

/** Convert snake_case to Title Case (e.g. distribution_board -> "Distribution Board"). */
function snakeToTitleCase(str) {
  if (!str || typeof str !== 'string') return '';
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/** Load More button for Phase 4.3 – appends next page of assets to avoid lag with 100+ rows. */
function LoadMoreButton({ hasNextPage, isFetchingNextPage, fetchNextPage }) {
  if (!hasNextPage) return null;

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem' }}>
      <Button
        type="button"
        variant="secondary"
        onClick={() => fetchNextPage()}
        disabled={isFetchingNextPage}
      >
        {isFetchingNextPage ? 'Loading more...' : 'Load More Assets'}
      </Button>
    </div>
  );
}

function Assets() {
  const {
    user,
    canManageAssets,
    searchQuery,
    setSearchQuery,
    debouncedSearch,
    filters,
    setFilters,
    sortBy,
    sortOrder,
    searchParams,
    setSearchParams,
    assets,
    allAssets,
    setAllAssets,
    assetsResponse,
    isLoading,
    isError,
    error,
    pagination,
    hasNextPage,
    setPage,
    sites,
    sitesError,
    selectedIds,
    setSelectedIds,
    selectedAsset,
    setSelectedAsset,
    showForm,
    setShowForm,
    editingAsset,
    formData,
    setFormData,
    showImportModal,
    setShowImportModal,
    importFile,
    setImportFile,
    importResult,
    setImportResult,
    importLoading,
    importAction,
    previewGeneratedCount,
    exportLoading,
    createMutation,
    updateMutation,
    deleteMutation,
    bulkDeleteMutation,
    resetForm,
    openEdit,
    handleSubmit,
    handleImportSubmit,
    handlePreviewClick,
    closeImportModal,
    handleExportAssetsCsv,
  } = useAssets();

  if (isError && error) {
    const isNetworkError = error?.code === 'ERR_NETWORK' || !error?.response;
    return (
      <div className="page-error">
        {isNetworkError ? (
          <>
            <p><strong>Cannot connect to the server.</strong></p>
            <p>Make sure the backend is running: in the <code>backend</code> folder run <code>npm run dev</code> (port 3000).</p>
          </>
        ) : (
          <p>Failed to load assets.</p>
        )}
      </div>
    );
  }

  if (user !== null && !canManageAssets) {
    return null;
  }

  return (
    <div className="page">
      <header className="settings-tab-header settings-tab-header--split">
        <div>
          <h1 className="settings-tab-title">Assets</h1>
          <p className="text-muted settings-tab-description">
            Organisation-wide asset registry. Filter by site below.
          </p>
        </div>
        <div className="settings-tab-header-actions">
          {canManageAssets && (
            <Button
              type="button"
              onClick={() => setShowImportModal(true)}
            >
              Import CSV
            </Button>
          )}
          {canManageAssets && (
            <Button
              type="button"
              onClick={handleExportAssetsCsv}
              disabled={exportLoading}
            >
              {exportLoading ? 'Exporting…' : 'Export CSV'}
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              resetForm();
              setFormData((prev) => ({ ...prev, siteId: filters.siteId || '' }));
              setShowForm(true);
            }}
          >
            Add Asset
          </Button>
        </div>
      </header>

      {selectedAsset && (
        <RecordTestModal
          key={selectedAsset.id}
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
          onAssetDetailsUpdated={(a) =>
            setSelectedAsset((prev) => (prev && prev.id === a.id ? { ...prev, ...a } : prev))
          }
        />
      )}

      <AssetImportModal
        isOpen={showImportModal}
        onClose={closeImportModal}
        importFile={importFile}
        setImportFile={setImportFile}
        importResult={importResult}
        setImportResult={setImportResult}
        importLoading={importLoading}
        importAction={importAction}
        onSubmit={handleImportSubmit}
        onPreview={handlePreviewClick}
        previewGeneratedCount={previewGeneratedCount}
      />

      <div className="filters">
        {sitesError && (
          <p className="text-muted" style={{ width: '100%', marginBottom: '0.5rem' }}>
            Failed to load site filters.
          </p>
        )}
        <input
          type="search"
          placeholder="Search by site, asset code, equipment code, building, room, type…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        <Select
          inline
          value={filters.siteId}
          onChange={(e) => setFilters({ ...filters, siteId: e.target.value })}
        >
          <option value="">All Sites</option>
          {sites?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.display_name ?? s.name ?? s.site_code ?? 'Unnamed site'}
            </option>
          ))}
        </Select>
        <Select
          inline
          value={filters.elementType}
          onChange={(e) => setFilters({ ...filters, elementType: e.target.value })}
        >
          <option value="">All Element Types</option>
          <option value="electrical">Electrical</option>
          <option value="fitments">Fitments</option>
        </Select>
        <Select
          inline
          value={filters.assetType}
          onChange={(e) => setFilters({ ...filters, assetType: e.target.value })}
        >
          <option value="">All Types</option>
          {ASSET_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
        <Select
          inline
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">All Status</option>
          <option value="overdue">Overdue</option>
          <option value="due_soon">Due Soon</option>
          <option value="current">Current</option>
          <option value="inactive">Inactive (End of Life)</option>
          <option value="removed">Removed / Disconnected</option>
        </Select>
        <Select
          inline
          value={sortBy}
          onChange={(e) => {
            const next = new URLSearchParams(searchParams);
            const v = e.target.value;
            if (v) { next.set('sortBy', v); next.set('sortOrder', sortOrder); } else { next.delete('sortBy'); next.delete('sortOrder'); }
            setSearchParams(next);
          }}
          title="Sort by column"
        >
          <option value="">Sort by…</option>
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>
        {sortBy && (
          <Select
            inline
            value={sortOrder}
            onChange={(e) => {
              const next = new URLSearchParams(searchParams);
              next.set('sortOrder', e.target.value);
              setSearchParams(next);
            }}
            title="Sort direction"
          >
            <option value="asc">A–Z / Oldest first</option>
            <option value="desc">Z–A / Newest first</option>
          </Select>
        )}
      </div>

      <AssetFormModal
        isOpen={showForm}
        onClose={resetForm}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        isEditing={!!editingAsset}
        isSubmitting={createMutation.isLoading || updateMutation.isLoading}
        sites={sites}
        electricalAssetTypes={ELECTRICAL_ASSET_TYPES}
        fitmentsAssetTypes={FITMENTS_ASSET_TYPES}
        assetFrequencyMap={ASSET_FREQUENCY_MAP}
        assetTypeDefaults={ASSET_TYPE_DEFAULTS}
        conditionRatings={CONDITION_RATINGS}
      />

      {canManageAssets && selectedIds.size > 0 && (
        <div className="bulk-action-bar">
          <span className="bulk-action-count">
            {selectedIds.size} asset{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <Button
            type="button"
            variant="danger"
            size="sm"
            disabled={bulkDeleteMutation.isLoading}
            onClick={() => {
              const count = selectedIds.size;
              if (window.confirm(`Permanently remove ${count} asset${count !== 1 ? 's' : ''}? This cannot be undone.`)) {
                bulkDeleteMutation.mutate([...selectedIds]);
              }
            }}
          >
            {bulkDeleteMutation.isLoading ? 'Deleting…' : 'Delete Selected'}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear
          </Button>
        </div>
      )}

      <div className="table-container assets-table-container">
        {(allAssets.length > 0 || pagination.totalCount > 0) && (
          <div className="assets-count-badge">
            Showing {allAssets.length} of {pagination.totalCount} assets
          </div>
        )}

        {/* Desktop: table (hidden on mobile) */}
        <AssetTableDesktop
          assets={assets ?? []}
          selectedIds={selectedIds}
          canManageAssets={canManageAssets}
          conditionRatings={CONDITION_RATINGS}
          snakeToTitleCase={snakeToTitleCase}
          showInitialSkeleton={(assetsResponse?.assets ?? allAssets).length === 0 && isLoading}
          showMoreSkeleton={isLoading && allAssets.length > 0}
          onSelectId={(id, checked) => {
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (checked) next.add(id);
              else next.delete(id);
              return next;
            });
          }}
          onSelectAll={(checked) => {
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (checked) assets.forEach((a) => next.add(a.id));
              else assets.forEach((a) => next.delete(a.id));
              return next;
            });
          }}
          onRecordTest={setSelectedAsset}
          onEdit={openEdit}
          onDelete={(asset) => {
            if (window.confirm('Delete this asset?')) deleteMutation.mutate(asset.id);
          }}
        />

        {/* Mobile: stacked cards (hidden on desktop) – no horizontal scroll */}
        <AssetMobileCards
          assets={assets ?? []}
          selectedIds={selectedIds}
          canManageAssets={canManageAssets}
          snakeToTitleCase={snakeToTitleCase}
          showInitialSkeleton={(assetsResponse?.assets ?? allAssets).length === 0 && isLoading}
          showMoreSkeleton={isLoading && allAssets.length > 0}
          onSelectId={(id, checked) => {
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (checked) next.add(id);
              else next.delete(id);
              return next;
            });
          }}
          onRecordTest={setSelectedAsset}
          onEdit={openEdit}
          onDelete={(asset) => {
            if (window.confirm('Delete this asset?')) deleteMutation.mutate(asset.id);
          }}
        />
        {!isLoading && assets.length === 0 && (debouncedSearch || filters.siteId || filters.assetType || filters.elementType) && (
          <div className="assets-empty-state">
            <p className="assets-empty-message">No assets found.</p>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setSearchQuery('');
                setFilters((prev) => ({ ...prev, siteId: '', assetType: '', elementType: '' }));
                setPage(1);
                setAllAssets([]);
              }}
            >
              Clear Search
            </Button>
          </div>
        )}
        {!isLoading && assets.length === 0 && !debouncedSearch && !filters.siteId && !filters.assetType && !filters.elementType && (
          <p className="empty-state">No assets found.</p>
        )}
        {(allAssets.length > 0 || hasNextPage) && (
          <div className="assets-pagination">
            <LoadMoreButton
              hasNextPage={hasNextPage}
              isFetchingNextPage={Boolean(isLoading && allAssets.length > 0)}
              fetchNextPage={() => setPage((p) => p + 1)}
            />
            <span className="assets-pagination-info">
              Showing {allAssets.length} of {pagination.totalCount} assets
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default Assets;
